import { useCallback, useState } from 'react';
import type { AutocompleteInputChangeReason } from '@mui/material';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * Shortest term the search APIs accept. Below it the endpoints answer 400, so
 * the client sends no `search` at all and gets the unsearched first page — which
 * is the right thing to show while someone is still typing the first letter.
 */
export const TYPEAHEAD_MIN_LENGTH = 2;

/**
 * Quiet period before a settled term is queried.
 *
 * 500 ms is the long end of the usable range for a type-ahead: enough that a
 * name typed at speed costs one request instead of five, short enough that the
 * list still feels like it is following you. Past roughly a second the field
 * reads as unresponsive rather than deliberate.
 */
export const TYPEAHEAD_DEBOUNCE_MS = 500;

export interface Typeahead {
  /**
   * What the input box shows. Distinct from {@link searchTerm}: after a pick this
   * holds the chosen option's label, which is a display string and not something
   * to search for.
   */
  inputValue: string;
  /** The raw term being searched for, updated on every keystroke. */
  term: string;
  /** Sets both the box and the term (for plain, non-MUI inputs). */
  setTerm: (next: string) => void;
  /** Clears the box, the term and the settled term. */
  reset: () => void;
  /**
   * The **settled** term to send to the server: debounced, trimmed, and `''`
   * whenever it is shorter than the minimum. Key the query on this.
   */
  searchTerm: string;
  /** True when {@link searchTerm} is a real search rather than the default page. */
  hasQuery: boolean;
  /**
   * True between a keystroke and the moment its term settles — i.e. the results
   * on screen are known to be for an older term. Prefer {@link typeaheadPage},
   * which folds this together with the request being in flight.
   */
  isDebouncing: boolean;
  /**
   * Adapter for MUI `Autocomplete`'s `onInputChange`. Pair it with
   * `inputValue={search.inputValue}` — the box has to be controlled for the
   * `'reset'` rule below to mean anything.
   *
   * - `'input'` — real typing. Drives the box and the search.
   * - `'clear'` — the clear button. Empties both.
   * - `'reset'` — MUI rewriting the box itself. It is **display only**: it never
   *   touches the search term. Two reasons. An *empty* reset is a blur with
   *   nothing picked, and taking it is what made the typed text vanish while the
   *   list kept answering a term you could no longer see (BUG-043). A *non-empty*
   *   reset is the selected option's label, which is a display string and not
   *   something to search for — and it is emitted from a lifecycle effect on any
   *   render while the field is unfocused, not just after a pick, so treating it
   *   as intent would let a re-render silently cancel someone's search.
   */
  onInputChange: (
    event: unknown,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
}

export interface UseTypeaheadOptions {
  /** Override the minimum searchable length. */
  minLength?: number;
  /** Override the debounce quiet period. */
  delayMs?: number;
}

/** Trims and gates a raw value into a server-ready term. */
function gate(value: string, minLength: number): string {
  const trimmed = value.trim();
  return trimmed.length >= minLength ? trimmed : '';
}

/**
 * The one type-ahead search convention for this app: **debounce the input, gate
 * it on a minimum length, query the server for the settled term, and never show
 * an older term's answer as though it were this one's.**
 *
 * Every user picker (and every server-searched list filter) owns one of these.
 *
 * ```tsx
 * const search = useTypeahead();
 * const query = useUserOptions(search.searchTerm);
 * const page = typeaheadPage(search, query);
 * <Autocomplete
 *   options={page.items}
 *   loading={page.loading}
 *   filterOptions={(o) => (page.awaiting ? [] : o)}
 *   inputValue={search.inputValue}
 *   onInputChange={search.onInputChange}
 * />
 * ```
 *
 * `filterOptions` must not re-filter: MUI's default runs over the rendered
 * label, which silently drops every hit the server matched on a field the label
 * doesn't show (an email or an employee number). Returning the options untouched
 * — except while {@link TypeaheadPage.awaiting} — is the whole contract.
 */
export function useTypeahead({
  minLength = TYPEAHEAD_MIN_LENGTH,
  delayMs = TYPEAHEAD_DEBOUNCE_MS,
}: UseTypeaheadOptions = {}): Typeahead {
  const [inputValue, setInputValue] = useState('');
  const [term, setTermState] = useState('');
  const debounced = useDebouncedValue(term, delayMs);

  const searchTerm = gate(debounced, minLength);
  // Compare the *gated* forms: typing a single character changes the raw term
  // but not the term that will be sent, so it is not a pending search.
  const isDebouncing = gate(term, minLength) !== searchTerm;

  const setTerm = useCallback((next: string) => {
    setInputValue(next);
    setTermState(next);
  }, []);

  const reset = useCallback(() => {
    setInputValue('');
    setTermState('');
  }, []);

  const onInputChange = useCallback(
    (_event: unknown, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') {
        setInputValue(value);
        setTermState(value);
        return;
      }
      if (reason === 'clear') {
        setInputValue('');
        setTermState('');
        return;
      }
      // 'reset' — display only; see the doc on Typeahead.onInputChange. Take a
      // label MUI has to show, ignore the blank it writes on a blur, and leave
      // the search term alone either way.
      if (reason === 'reset' && value !== '') {
        setInputValue(value);
      }
    },
    [],
  );

  return {
    inputValue,
    term,
    setTerm,
    reset,
    searchTerm,
    hasQuery: searchTerm.length > 0,
    isDebouncing,
    onInputChange,
  };
}

/** The minimum a query result must expose for {@link typeaheadPage} to judge it. */
export interface TypeaheadQuery<T> {
  data?: { items: T[]; appliedTerm: string; total?: number; hasMore?: boolean };
  isFetching: boolean;
  isError?: boolean;
}

export interface TypeaheadPage<T> {
  /**
   * The rows that answer the term currently in the box — **empty** whenever the
   * page on hand was fetched for a different one.
   */
  items: T[];
  /**
   * The server's match count for the term in the box, or `0` until a page for it
   * arrives. Reconciled for the same reason as {@link items}: read raw off the
   * query it describes some other term's page, which is how a picker ends up
   * captioned "Showing 0 of 25".
   */
  total: number;
  /** Whether the cap hid matches for the term in the box; `false` until settled. */
  hasMore: boolean;
  /** True while the term is settling or its page is in flight. */
  loading: boolean;
  /**
   * True when there is nothing valid to show yet. Feed it to `filterOptions` so
   * the dropdown renders its loading text instead of a previous term's rows.
   */
  awaiting: boolean;
}

/** Shared so a not-settled render doesn't allocate a fresh array every time. */
const NO_ITEMS: readonly never[] = [];

/**
 * Reconciles a type-ahead's term with the page a query actually has in hand.
 *
 * **Why this is not just `isFetching`.** Two windows exist where the rows on
 * screen do not answer what has been typed, and neither is covered by the query
 * being in flight: the debounce window (the key has not changed yet, so nothing
 * is fetching) and `keepPreviousData` (which deliberately holds the previous
 * term's page so the list doesn't flash empty). Both are fine as long as the
 * stale rows are not *rendered as results* — but the server-searched pickers
 * disable MUI's client-side filter, so they were rendering them verbatim under a
 * term they did not match. Typing "Riley" listed people with no Riley in them
 * (BUG-043); before the pickers moved server-side, MUI's filter had been hiding
 * the same staleness by accident.
 *
 * Comparing `appliedTerm` — the term the page was fetched for — against the term
 * in the box closes both windows with one condition.
 */
export function typeaheadPage<T>(search: Typeahead, query: TypeaheadQuery<T>): TypeaheadPage<T> {
  const applied = query.data?.appliedTerm;

  // A page that arrived without an `appliedTerm` cannot be judged — a hook that
  // forgot to stamp it, or a hot-reloaded module pairing new code with an old one.
  // Show it rather than withhold it: the cost of rendering a possibly-stale page is
  // the bug this function exists to prevent, but the cost of withholding one is a
  // picker wedged in "Searching…" forever with the data sitting right there, and no
  // error to explain it. Degrade toward showing data, and say so out loud in dev.
  const unjudgeable = query.data !== undefined && applied === undefined;
  if (unjudgeable && import.meta.env.DEV) {
    console.warn(
      '[typeahead] page has no `appliedTerm`; staleness cannot be detected. ' +
        'The query hook must return it — see useUserOptions.',
    );
  }

  // `searchTerm` is the *debounced* term, so `applied === searchTerm` is still
  // true throughout the debounce window — the page matches the term that has been
  // sent, not the one being typed. `isDebouncing` is the other half, and without
  // it the previous answer renders for the whole quiet period.
  const settled =
    query.data !== undefined &&
    (unjudgeable || (applied === search.searchTerm && !search.isDebouncing));

  // A failed request drops the placeholder data, so `settled` can never become
  // true again. Without this the picker spins forever and its error copy is
  // unreachable — the caller's `noOptionsText` only renders once loading stops.
  const failed = query.isError === true && query.data === undefined;

  return {
    items: settled ? (query.data?.items ?? []) : (NO_ITEMS as unknown as T[]),
    total: settled ? (query.data?.total ?? 0) : 0,
    hasMore: settled ? (query.data?.hasMore ?? false) : false,
    loading: !failed && (!settled || query.isFetching),
    // A background refetch of an up-to-date page is not "awaiting" — the rows on
    // screen are still the right answer, so they keep rendering.
    awaiting: !failed && !settled,
  };
}
